import React from 'react';
import { format } from 'date-fns';
import { GetServerSideProps } from 'next';
import { useQuery } from 'urql';

import Breadcrumbs from '@/components/Breadcrumbs';
import HeroImageFit from '@/components/HeroImageFit';
import HeroImage from '@/components/layout/HeroImage';
import { renderPageContent } from '@/lib/ContentRenderer';
import { getServerSidePropsWithGlobalData } from '@/lib/getServerSideProps';
import { extractFragment, extractItems } from '@/lib/GraphQLHelper';
import { withUrql } from '@/lib/urqlClient';
import { ErrorPage } from '@/pages/_error';

import query from './query.graphql';

import styles from './styles.module.scss';

interface Props {
  slug: string;
}

const NewsArticle = ({ slug }: Props) => {
  const [{ data, error, fetching }] = useQuery<CompetitionsEntryQuery>({
    query,
    variables: { slug },
  });

  const entry = extractFragment<CompetitionsEntryFragment>(data?.entry);
  const pageContent = extractItems<Craft.PageContent>(entry?.pageContent);

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  if (!fetching && !data?.entry) {
    return <ErrorPage statusCode={404} />;
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Competitions', uri: '/competitions' },
    { title: entry?.title || '' },
  ];

  return (
    <article className={styles.competitionsEntry} data-testid="competition-page">
      <HeroImage image={entry?.heroImage?.[0]?.url} height={454} />
      <div className={styles.heroContent}>
        <div className={styles.body}>
          <strong>Competitions</strong>
          <h3>{entry?.title}</h3>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <div dangerouslySetInnerHTML={{ __html: entry?.body || '' }} />
          {entry?.expiryDate && <strong>Competition ends: {format(new Date(entry.expiryDate), 'Mo MMMM yyyy')}</strong>}
        </div>
        {entry?.heroImage?.[0]?.url && <HeroImageFit src={entry.heroImage[0].url} />}
      </div>
      <div className={styles.pageContent}>{pageContent?.map((row) => renderPageContent(row!))}</div>
    </article>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) =>
  await getServerSidePropsWithGlobalData(ctx, async (ctx) => {
    const { urqlClient, nextCtx } = ctx;
    const slug = nextCtx?.query?.slug;

    await urqlClient
      .query<CompetitionsEntryQuery, CompetitionsEntryQueryVariables>(query, {
        slug,
      })
      .toPromise();

    return { props: { slug } };
  });

export default withUrql(NewsArticle);
